import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Client bindings for the deal blast engine (server/blast-routes.ts).

export interface WarmupInfo {
  active: boolean;
  startedAt: string | null;
  day: number;
  totalDays: number;
  cap: number;
  targetCap: number;
}

export interface BlastMailbox {
  id: string;
  ownerUserId: string;
  ownerName: string;
  fromAddress: string;
  displayName: string | null;
  signatureHtml: string | null;
  dailyCap: number;
  isActive: boolean;
  hasToken: boolean;
  sent24h: number;
  isMainDomain: boolean;
  mainDomain: string;
  capToday: number;
  warmup: WarmupInfo;
}

export interface BlastCampaign {
  id: string;
  name: string;
  campaign_type: "teaser" | "cim" | "custom" | "market_outreach";
  ai_angle?: string | null;
  personalization?: "template" | "personalized" | null;
  client_id: string | null;
  client_name: string | null;
  subject: string;
  body_html: string;
  attachments: { objectPath: string; filename: string; size: number }[] | null;
  cc_addresses?: string[] | null;
  bcc_addresses?: string[] | null;
  mailbox_ids?: string[] | null;
  status: "draft" | "scheduled" | "sending" | "paused" | "completed" | "cancelled";
  send_window_start: number;
  send_window_end: number;
  send_days: number[] | null;
  timezone: string | null;
  pacing: "steady" | "fast" | null;
  scheduled_start_at: string | null;
  created_by_name?: string | null;
  created_at: string;
  launched_at: string | null;
  completed_at: string | null;
  total: number;
  sent: number;
  pending: number;
  failed: number;
  replied: number;
  skipped: number;
}

export interface BlastRecipient {
  id: string;
  contact_id: string | null;
  company_id: string | null;
  company_name: string | null;
  email: string;
  name: string | null;
  status: "pending" | "sent" | "failed" | "replied" | "skipped" | "bounced";
  sent_at: string | null;
  sent_from: string | null;
  error: string | null;
  context?: string | null;
  custom_subject?: string | null;
  custom_body?: string | null;
  draft_status?: "draft" | "approved" | null;
  lead_hook?: string | null;
  draft_style?: string | null;
}

export interface RecipientReport {
  added: number;
  noEmail: string[];
  suppressed: string[];
  duplicates: number;
}

async function j<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as any).error || `Request failed (${res.status})`);
  return body as T;
}

const post = (url: string, body?: unknown) =>
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

export interface ComposeMeta {
  complianceFooterHtml: string;
  logoDataUri: string;
  mainDomain: string;
  mainDomainCap: number;
}

export function useComposeMeta() {
  return useQuery<ComposeMeta>({
    queryKey: ["blast", "compose-meta"],
    queryFn: async () => j(await fetch("/api/blast/compose-meta", { credentials: "include" })),
    staleTime: Infinity,
  });
}

export function useBlastMailboxes() {
  return useQuery<BlastMailbox[]>({
    queryKey: ["blast", "mailboxes"],
    queryFn: async () => j(await fetch("/api/blast/mailboxes", { credentials: "include" })),
  });
}

export interface MailboxHealthRow {
  id: string;
  fromAddress: string;
  displayName: string | null;
  ownerName: string;
  domain: string;
  isActive: boolean;
  pausedReason: string | null;
  sentToday: number;
  sent7d: number;
  sentTotal: number;
  lastSendAt: string | null;
  bounces7d: number;
  bouncesTotal: number;
  replies7d: number;
  repliesTotal: number;
  autoreplies7d: number;
  bounceRate7d: number;
  replyRate7d: number;
  capToday: number;
  warmup: { active: boolean; day: number; totalDays: number; cap: number; targetCap: number };
  lastScanAt: string | null;
  lastScanError: string | null;
  health: { score: number | null; grade: "A" | "B" | "C" | "D" | "new"; factors: string[] };
}

export function useMailboxHealth() {
  return useQuery<MailboxHealthRow[]>({
    queryKey: ["blast", "mailbox-health"],
    queryFn: async () => j(await fetch("/api/blast/mailboxes/health", { credentials: "include" })),
    refetchInterval: 45_000,
  });
}

export function useBlastCampaigns() {
  return useQuery<BlastCampaign[]>({
    queryKey: ["blast", "campaigns"],
    queryFn: async () => j(await fetch("/api/blast/campaigns", { credentials: "include" })),
    refetchInterval: 30_000, // live-ish progress on the list page
  });
}

export function useBlastCampaign(id: string | null) {
  return useQuery<BlastCampaign & { recipients: BlastRecipient[] }>({
    queryKey: ["blast", "campaign", id],
    queryFn: async () => j(await fetch(`/api/blast/campaigns/${id}`, { credentials: "include" })),
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.status === "sending" ? 15_000 : false),
  });
}

export function useBlastMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["blast"] });

  return {
    createMailbox: useMutation({
      mutationFn: async (body: Partial<BlastMailbox> & { fromAddress: string }) =>
        j<BlastMailbox>(await post("/api/blast/mailboxes", body)),
      onSuccess: invalidate,
    }),
    updateMailbox: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        j(await fetch(`/api/blast/mailboxes/${id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        })),
      onSuccess: invalidate,
    }),
    deleteMailbox: useMutation({
      mutationFn: async (id: string) =>
        j(await fetch(`/api/blast/mailboxes/${id}`, { method: "DELETE", credentials: "include" })),
      onSuccess: invalidate,
    }),
    testMailbox: useMutation({
      mutationFn: async (id: string) => j<{ sentTo: string }>(await post(`/api/blast/mailboxes/${id}/test`)),
    }),
    scanMailboxesNow: useMutation({
      mutationFn: async () => j(await post("/api/blast/mailboxes/scan-now")),
      onSuccess: invalidate,
    }),
    createCampaign: useMutation({
      mutationFn: async (body: Record<string, unknown>) => j<BlastCampaign>(await post("/api/blast/campaigns", body)),
      onSuccess: invalidate,
    }),
    updateCampaign: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        j(await fetch(`/api/blast/campaigns/${id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        })),
      onSuccess: invalidate,
    }),
    deleteCampaign: useMutation({
      mutationFn: async (id: string) =>
        j(await fetch(`/api/blast/campaigns/${id}`, { method: "DELETE", credentials: "include" })),
      onSuccess: invalidate,
    }),
    buildRecipients: useMutation({
      mutationFn: async ({ id, ...body }: { id: string; source?: string; contactIds?: string[] }) =>
        j<RecipientReport>(await post(`/api/blast/campaigns/${id}/recipients`, body)),
      onSuccess: invalidate,
    }),
    removeRecipient: useMutation({
      mutationFn: async ({ campaignId, recipientId }: { campaignId: string; recipientId: string }) =>
        j(await fetch(`/api/blast/campaigns/${campaignId}/recipients/${recipientId}`, { method: "DELETE", credentials: "include" })),
      onSuccess: invalidate,
    }),
    updateRecipient: useMutation({
      mutationFn: async ({ campaignId, recipientId, ...body }: { campaignId: string; recipientId: string } & Record<string, unknown>) =>
        j<BlastRecipient>(await fetch(`/api/blast/campaigns/${campaignId}/recipients/${recipientId}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        })),
      onSuccess: invalidate,
    }),
    suggestFallback: useMutation({
      mutationFn: async (body: { angle: string; listId?: string }) =>
        j<{ name: string; subject: string; body: string }>(await post("/api/blast/suggest-fallback", body)),
    }),
    approveAllDrafts: useMutation({
      mutationFn: async (id: string) => j<{ approved: number }>(await post(`/api/blast/campaigns/${id}/approve-drafts`)),
      onSuccess: invalidate,
    }),
    clearDrafts: useMutation({
      mutationFn: async (id: string) => j<{ cleared: number }>(await post(`/api/blast/campaigns/${id}/clear-drafts`)),
      onSuccess: invalidate,
    }),
    generateDrafts: useMutation({
      mutationFn: async ({ id, recipientIds }: { id: string; recipientIds?: string[] }) =>
        j<{ generated: number; failed: string[] }>(await post(`/api/blast/campaigns/${id}/generate-drafts`, recipientIds ? { recipientIds } : {})),
      onSuccess: invalidate,
    }),
    lifecycle: useMutation({
      mutationFn: async ({ id, action }: { id: string; action: "launch" | "pause" | "resume" | "cancel" }) =>
        j(await post(`/api/blast/campaigns/${id}/${action}`)),
      onSuccess: invalidate,
    }),
    testSend: useMutation({
      mutationFn: async (id: string) => j<{ sentTo: string }>(await post(`/api/blast/campaigns/${id}/test-send`)),
    }),
  };
}
